import { useEffect, useState } from "react";
import { t } from "i18next";
import { Form } from "../../organisms/form/Form";
import { Headings } from "@sentara-health/optimahealth_web";
import UnpaidGroupBalanceForm from "./UnpaidGroupBalanceForm";
import {
  PastDueBalanceSummaryItem,
  PastDueBalanceDetailsItem,
  usePastDueBalanceSummary,
  usePastDueBalanceDetails,
} from "@sentara-health/sentara-api-hooks-core";
import { useNavigate, useParams } from "react-router";
import { UNPAID_GROUP_BALANCE } from "utils/constants/Routes";
import { useSearchParams } from "react-router-dom";
import { useGlobalState, useAuth, useBranding, Brands } from "providers";
import { spanDaysDefault } from "utils/constants/Constants";
import UnpaidGroupBalanceTable from "./UnpaidGroupBalanceTable";
import UnpaidGroupBalanceHeader from "./UnpaidGroupBalanceHeader";
import UnpaidGroupBalanceDetails from "./UnpaidGroupBalanceDetails";
import { parse } from "path";

const showUnLocate = ({
  isSubmited,
  pastDueSummary,
  spanDays,
  pastDueState,
}: {
  isSubmited: boolean;
  pastDueSummary: Array<PastDueBalanceSummaryItem>;
  spanDays: string;
  pastDueState: Array<PastDueBalanceDetailsItem>;
}) => {
  return !!(
    (!isSubmited && !pastDueSummary.length && spanDays) ||
    (!isSubmited && !pastDueState.length)
  );
};

export const UnpaidGroupBalance = () => {
  const { user } = useAuth();
  const { brand } = useBranding();
  const [overDuePeriod, setOverDuePeriod] = useState<string>("");

  const {
    getPastDueBalanceSummary,
    status: pastDueStatus,
    state: pastDueSummary,
  } = usePastDueBalanceSummary();

  const navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();
  const [groupIDValue, setGroupIDValue] = useState<any>(
    searchParams.get("accountId")
      ? searchParams.get("accountId")
      : searchParams.get("groupNumber") || "",
  );
  const { setPageLoader } = useGlobalState();
  const { groupId } = useParams();
  const { groupNumber } = useParams();
  const { accountId } = useParams();

  const {
    getPastDueBalanceDetails,
    state: pastDueState,
    status: pastDueApiStatus,
  } = usePastDueBalanceDetails();

  const [isSubmited, setIsSubmited] = useState<boolean>(true);
  const [shouldUsePastDueState, setShouldUsePastDueState] =
    useState<boolean>(true);

  const defaultValues = {
    groupNumber: "",
    overduePeriod: "",
    accountId: "",
  };

  useEffect(() => {
    document.title = t("pageTitleADA.unpaidGroupBalance");
    if (isSubmited && searchParams.get("spanDays")) {
      setPageLoader("unpaidBalance", true);
      let searchDays: any = searchParams.get("spanDays")
        ? searchParams.get("spanDays")
        : spanDaysDefault;
      let userCarrierID: any = user?.carrier?.carrierID ?? 0;
      try {
        getPastDueBalanceSummary({
          spanDays: parseInt(searchDays),
          carrierID: parseInt(userCarrierID),
          brokerID: user?.loginID ?? "",
          groupID: searchParams.get("groupNumber")
            ? searchParams.get("groupNumber")
            : (groupIDValue ?? ""),
        }).then(() => {
          setIsSubmited(false);
          setPageLoader("unpaidBalance", false);
        });
      } catch {
        navigate(`/app/${UNPAID_GROUP_BALANCE}`);
        setIsSubmited(false);
      }
    } else if (
      (searchParams.get("groupNumber") ||
        searchParams.get("accountId") ||
        groupId) &&
      !searchParams.get("spanDays")
    ) {
      setPageLoader("unpaidDetails", true);
      try {
        let groupIDValue = searchParams.get("groupNumber");
        // ? searchParams.get("accountId")
        // : searchParams.get("groupNumber");
        restorePastDueState();

        // Get groups for the selected account
        const selectedAccountId = searchParams.get("accountId");
        const filteredGroups = selectedAccountId
          ? (user?.groups?.filter((g) => g.accountId === selectedAccountId) ??
            [])
          : (user?.groups ?? []);
        const accountGroups = filteredGroups.map((g) => g.groupID);

        const selectedGroupAccountId = searchParams.get("groupNumber")
          ? (user?.groups?.find(
              (g) => g.groupID === searchParams.get("groupNumber"),
            )?.accountId ?? selectedAccountId)
          : undefined;

        getPastDueBalanceDetails({
          brokerID: user?.loginID ?? "",
          carrierID: String(user?.carrier?.carrierID ?? 0),
          groupID:
            brand === Brands.AVMED && selectedAccountId
              ? selectedGroupAccountId
                ? [selectedGroupAccountId]
                : [selectedAccountId]
              : groupIDValue
                ? [groupIDValue]
                : groupId
                  ? [groupId]
                  : accountGroups,
        }).then((res) => {
          setIsSubmited(false);
          setPageLoader("unpaidDetails", false);
          if (!res?.data?.result?.length) {
            navigate(`/app/${UNPAID_GROUP_BALANCE}`);
          }
        });
      } catch {
        setPageLoader("unpaidDetails", false);
      }
    }
    setIsSubmited(false);
    let pathName = window.location.pathname;
    let pathHref = window.location.href;
    let searchStringLength = pathName.split("/")?.length;
    // console.log("called__ length",searchStringLength);
    // console.log("called__ path",!pathHref.includes('?'));
    if (!pathHref.includes("?") && searchStringLength === 3) {
      clearPastDueState();
    }
  }, [searchParams, isSubmited, groupIDValue]);

  useEffect(() => {
    if (pastDueStatus === "success" || pastDueApiStatus === "success") {
      const ele = document.querySelector(".table-responsive") as HTMLDivElement;
      ele?.scrollIntoView({ behavior: "smooth" });
    }
  }, [pastDueStatus, pastDueApiStatus]);

  const onSubmit = (data: any) => {
    setIsSubmited(true);
    const query: {
      spanDays?: string;
      groupNumber?: string;
      accountId?: string;
      accountName?: string;
    } = {};
    if (data.overduePeriod) {
      query.spanDays = data.overduePeriod;
    }
    if (data.groupNumber) {
      query.groupNumber = data.groupNumber;
    }
    if (data.accountId) {
      query.accountId = data.accountId.split("-")[0];
      query.accountName = data.accountId.split("-")[1];
    }

    setSearchParams(query);
    if (brand === Brands.AVMED && data.accountId) {
      setGroupIDValue(query.accountId!);
    } else {
      setGroupIDValue(data.accountId ? data.accountId : data.groupNumber);
    }
  };

  const clearPastDueState = () => {
    setShouldUsePastDueState(false);
  };

  const restorePastDueState = () => {
    setShouldUsePastDueState(true);
  };

  const effectivePastDueState = shouldUsePastDueState ? pastDueState : [];

  return (groupIDValue !== "" || groupId) &&
    !searchParams.get("spanDays") &&
    !isSubmited &&
    effectivePastDueState.length ? (
    <UnpaidGroupBalanceDetails
      state={effectivePastDueState}
      groupId={groupIDValue ?? groupId}
      clearPastDueState={clearPastDueState}
      accountName={searchParams.get("accountName") || ""}
    />
  ) : (
    <div>
      {searchParams.get("spanDays") && pastDueSummary.length && !isSubmited ? (
        <div>
          <UnpaidGroupBalanceHeader />
          <UnpaidGroupBalanceTable
            setIsSubmited={setIsSubmited}
            status={pastDueStatus}
            pastDueSummary={pastDueSummary}
            spanDays={searchParams.get("spanDays")}
          />
        </div>
      ) : (
        <div>
          <UnpaidGroupBalanceHeader />
          <Headings
            className="font-twentyfour mb-4"
            dataTestId="txtSearchOptionsSubHeading"
            level={2}
            text={t("unpaidGroupBalance.subTitle.searchOptions")}
            variant={"medium"}
          />
          <div
            className="border light-grey-bg border-radius10 p-3 mb-4"
            data-testid="txtSearchInstructions"
          >
            <strong>{t("unpaidGroupBalance.content.instructions")}</strong>:{" "}
            {t("unpaidGroupBalance.content.descText1")}
          </div>

          <Form defaultValues={defaultValues} onSubmit={onSubmit}>
            <UnpaidGroupBalanceForm
              setOverDuePeriod={setOverDuePeriod}
              showUnLocate={showUnLocate({
                isSubmited,
                pastDueSummary,
                spanDays: searchParams.get("spanDays") ?? "",
                pastDueState,
              })}
            />
          </Form>
        </div>
      )}
    </div>
  );
};

export default UnpaidGroupBalance;
